import React, { useEffect, useRef, useState } from "react";
import copy from "../copy";
import { STEPS, nextAfter } from "../flow";
import { ANALYTICS_URL, FORM_ID, SHEET_SOURCE } from "../config";
import { formatDateTime } from "../helper/TimeFormat";
import { hashSHA256 } from "../helper/Encrypter";
import { getAdParams, fetchVisitorIp } from "../helper/tracking";
import EnergyStep from "../components/EnergyStep";
import ChoiceStep from "../components/ChoiceStep";
import InfoStep from "../components/InfoStep";
import PostalStep from "../components/PostalStep";
import BirthYearStep from "../components/BirthYearStep";
import EmailStep from "../components/EmailStep";
import PhoneStep from "../components/PhoneStep";
import NameStep from "../components/NameStep";
import ConsentStep from "../components/ConsentStep";
import ThankYou from "../components/ThankYou";

const track = (event, params) => {
  if (window.fbq) window.fbq("trackCustom", event, params);
};

const getInitialData = () => {
  const ads = getAdParams();
  return {
    energy_type: "",
    house_type: "",
    consumption_knowledge: "",
    yearly_kwh_range: "",
    contracted_power_range: "",
    sqm_range: "",
    people_at_home: "",
    monthly_bill: "",
    postal_code: "",
    place_name: "",
    provider: "",
    birth_year: "",
    email: "",
    phone: "",
    nombre: "",
    apellido: "",
    apeillidos: "",
    data_time: formatDateTime(),
    web_url: window.location.href,
    campaign_name: ads.campaign_name,
    adset_name: ads.adset_name,
    ad_name: ads.ad_name,
    user_ip: ads.user_ip,
  };
};

const Home = () => {
  const [step, setStep] = useState("energy");
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(getInitialData);
  const sessionIdRef = useRef(
    Math.random().toString(36).substring(2) + Date.now().toString(36)
  );
  const isFirstRender = useRef(true);

  const handleChange = (field, value) => {
    setData((prev) =>
      field === "apellido"
        ? { ...prev, apellido: value, apeillidos: value }
        : { ...prev, [field]: value }
    );
  };

  // ---- navigation (with a history stack so "Atrás" follows the real path) ----
  const goTo = (next) => {
    if (!next) return;
    setHistory((h) => [...h, step]);
    setStep(next);
  };

  const goBack = () => {
    if (!history.length) return;
    setStep(history[history.length - 1]);
    setHistory(history.slice(0, -1));
  };

  const advance = (value) => goTo(nextAfter(step, value));

  const choose = (field) => (value) => {
    track("involve.me_QuestionAnswer", { answer: value });
    handleChange(field, value);
    advance(value);
  };

  // ---- tracking ----
  useEffect(() => {
    track("involve.me_ProjectLoaded");
    // Visitor IP for the lead (unless the link already carried one).
    if (!getAdParams().user_ip) {
      fetchVisitorIp().then((ip) => {
        if (ip) setData((prev) => (prev.user_ip ? prev : { ...prev, user_ip: ip }));
      });
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      track("involve.me_QuestionProgress", { step: STEPS[step].n });
    }

    fetch(`${ANALYTICS_URL}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: sessionIdRef.current,
        form_id: FORM_ID,
        step: STEPS[step].n,
        step_name: STEPS[step].name,
        campaign_name: data.campaign_name,
        adset_name: data.adset_name,
        ad_name: data.ad_name,
      }),
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ---- submission ----
  const submitToGoogleSheet = () => {
    const url = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
    if (!url) {
      console.warn("REACT_APP_GOOGLE_SCRIPT_URL is not set; skipping Sheet.");
      return;
    }
    const payload = {
      firstName: data.nombre,
      lastName: data.apeillidos,
      email: data.email,
      phone: data.phone,
      campaign_name: data.campaign_name,
      adset_name: data.adset_name,
      ad_name: data.ad_name,
      energia: data.energy_type,
      tipo_vivienda: data.house_type,
      kwh_conocido: data.consumption_knowledge,
      kwh_aproximado: data.yearly_kwh_range,
      potencia: data.contracted_power_range,
      metros: data.sqm_range,
      personas: data.people_at_home,
      factura_mensual: data.monthly_bill,
      postal_code: data.postal_code,
      proveedor: data.provider,
      birth_year: data.birth_year,
      user_ip: data.user_ip,
      source: SHEET_SOURCE,
    };
    // Fire and forget: Apps Script doesn't answer CORS preflights.
    fetch(url, {
      method: "POST",
      mode: "no-cors",
      redirect: "follow",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch((error) => console.error("Google Script error:", error));
  };

  const sendEventToMeta = async () => {
    const id = process.env.REACT_APP_META_PIXEL_ID;
    const token = process.env.REACT_APP_META_PIXEL_TOKEN;
    if (!id || !token) return;

    const hashed = async (value) => (value ? hashSHA256(value) : undefined);
    const userData = {
      client_user_agent: navigator.userAgent,
      fn: await hashed(data.nombre),
      ln: await hashed(data.apellido),
      ph: await hashed(data.phone),
      em: await hashed(data.email),
      ct: await hashed(data.place_name),
      zip: await hashed(data.postal_code),
    };
    if (data.user_ip) userData.client_ip_address = data.user_ip;

    await fetch(
      `https://graph.facebook.com/v18.0/${id}/events?access_token=${token}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          data: [
            {
              event_name: "Lead",
              event_time: Math.floor(Date.now() / 1000),
              action_source: "website",
              event_source_url: window.location.href,
              user_data: userData,
            },
          ],
        }),
      }
    );
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      try {
        await sendEventToMeta();
      } catch (metaError) {
        console.error("Meta conversion API error:", metaError);
      }

      submitToGoogleSheet();

      // The analytics service is only for reporting and duplicate detection.
      // The lead has already gone to the Sheet and Meta, so if analytics is
      // down the person still gets the thank-you page (and can't double-submit).
      try {
        const response = await fetch(`${ANALYTICS_URL}/submit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...data,
            session_id: sessionIdRef.current,
            form_id: FORM_ID,
          }),
        });
        const result = await response.json().catch(() => ({}));

        if (response.ok && result?.ok === false && result?.reason === "duplicate") {
          return { ok: false, duplicate: true };
        }
      } catch (analyticsError) {
        console.error("Analytics submit failed (lead already sent):", analyticsError);
      }

      track("involve.me_ProjectCompleted");
      setHistory([]);
      setStep("thanks");
      return { ok: true };
    } catch (error) {
      console.error("Submission error:", error);
      return { ok: false };
    } finally {
      setSubmitting(false);
    }
  };

  // ---- rendering ----
  const renderStep = () => {
    const choice = copy.choices[step];
    if (choice) {
      return (
        <ChoiceStep
          config={choice}
          value={data[choice.field]}
          onSelect={choose(choice.field)}
          onBack={goBack}
        />
      );
    }

    switch (step) {
      case "energy":
        return (
          <EnergyStep
            value={data[copy.energy.field]}
            onSelect={choose(copy.energy.field)}
          />
        );
      case "estimate":
        return (
          <InfoStep
            title={copy.estimate.title}
            text={copy.estimate.text}
            button={copy.continue}
            onNext={() => advance()}
            onBack={goBack}
          />
        );
      case "postal":
        return (
          <PostalStep
            data={data}
            handleChange={handleChange}
            onNext={() => advance()}
            onBack={goBack}
          />
        );
      case "ineligible":
        return (
          <InfoStep
            title={copy.ineligible.title}
            text={copy.ineligible.text}
          />
        );
      case "birth":
        return (
          <BirthYearStep
            data={data}
            handleChange={handleChange}
            onNext={() => advance()}
            onBack={goBack}
          />
        );
      case "goodNews":
        return (
          <InfoStep
            title={copy.goodNews.title}
            text={copy.goodNews.text}
            note={copy.goodNews.note}
            button={copy.continue}
            onNext={() => advance()}
            onBack={goBack}
          />
        );
      case "email":
        return (
          <EmailStep
            data={data}
            handleChange={handleChange}
            onNext={() => advance()}
            onBack={goBack}
          />
        );
      case "phone":
        return (
          <PhoneStep
            data={data}
            handleChange={handleChange}
            onNext={() => {
              track("involve.me_ContactFormFilled");
              advance();
            }}
            onBack={goBack}
          />
        );
      case "name":
        return (
          <NameStep
            data={data}
            handleChange={handleChange}
            onNext={() => advance()}
            onBack={goBack}
          />
        );
      case "consent":
        return (
          <ConsentStep
            onSubmit={handleFinalSubmit}
            submitting={submitting}
            onBack={goBack}
          />
        );
      case "thanks":
        return <ThankYou />;
      default:
        return null;
    }
  };

  const progress = STEPS[step].progress;

  return (
    <div className="rp-app">
      <header className="rp-header">
        <div className="rp-header__inner">
          <img
            className="rp-logo"
            src={`${process.env.PUBLIC_URL}/repsol-logo.png`}
            alt="Repsol"
            width="111"
            height="30"
          />
        </div>
      </header>
      {step !== "thanks" && (
        <div
          className="rp-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div className="rp-progress__bar" style={{ width: `${progress}%` }} />
        </div>
      )}
      <main className="rp-main">{renderStep()}</main>
    </div>
  );
};

export default Home;
